
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import styles from "./crewWeekGrid.module.css";
import moment from "moment";
import { Text } from "ui/Typography";
import { useSelector } from "react-redux";
import EventBlock from "./EventBlock";
import { getEventColor, getEventBorderColor } from "pagesV2/pro/Calendar/utils";
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails";
import { useRefreshCalendarBookings } from "pagesV2/pro/Calendar/useRefreshCalendarBookings";

const isEventForCrewAndDay = (ev, crew, day) =>
  moment(ev.date).isSame(day, "day") &&
  (ev.assigned_crew || []).some((c) => c.id === crew.id || c.name === crew.name)

const buildCrewDayMap = (crew, calendarDays, events) => {
  const dayMap = {}
  calendarDays.forEach((day) => {
    const dayStr = day.format("YYYY-MM-DD")
    dayMap[dayStr] = events.filter((ev) => isEventForCrewAndDay(ev, crew, day)).map(formatEventLabels)
  })
  return dayMap
}

const formatEventLabels = (ev) => {
  const startMoment = ev.start ? moment(ev.start, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  const endMoment = ev.end ? moment(ev.end, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  return {
    ...ev,
    startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
    endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end,
  }
}

// CrewWeekGrid: Crew as rows, days as columns, table layout
const CrewWeekGrid = ({ calendarDays, events }) => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const calendarCrew = useSelector((state) => state.staff?.calendarCrew || []);

  const crewMembers = React.useMemo(() =>
    calendarCrew
      .filter((c) => c.status?.toLowerCase() === "active")
      .map((c) => ({ id: c.id, name: c.name }))
  , [calendarCrew]);

  const eventsByCrewAndDay = useMemo(() => {
    const map = {};
    crewMembers.forEach((crew) => {
      map[crew.id] = buildCrewDayMap(crew, calendarDays, events);
    });
    return map;
  }, [crewMembers, calendarDays, events]);

  const refreshCalendarBookings = useRefreshCalendarBookings()
  const handleBookingCancel = () => {
    setIsBookingModalOpen(false);
    setSelectedBookingId(null);
  };
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  return (
    <div className={styles.crewWeekGrid}>
      <table className={styles.crewWeekTable}>
        <thead>
          <tr className={styles.crewWeekHeaderRow}>
            <th className={styles.crewWeekHeaderCell}>Crew Member</th>
            {calendarDays.map((day) => (
              <th key={day.format("YYYY-MM-DD")} className={styles.crewWeekHeaderCell}>
                <Text fw="500">{day.format("ddd D")}</Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {crewMembers.length === 0 ? (
            <tr className={styles.crewWeekRow}>
              <td colSpan={calendarDays.length + 1} className={styles.crewWeekCrewCell}>
                No crew members found.
              </td>
            </tr>
          ) : crewMembers.map((crew) => (
            <tr key={crew.id} className={styles.crewWeekRow}>
              <td className={styles.crewWeekCrewCell}>
                <Text>{crew.name}</Text>
              </td>
              {calendarDays.map((day) => {
                const dayStr = day.format("YYYY-MM-DD");
                const cellEvents = eventsByCrewAndDay[crew.id]?.[dayStr] || [];
                return (
                  <td key={dayStr} className={styles.crewWeekEventCell}>
                    <div className={styles.crewWeekEventCellInner}>
                      {cellEvents.map((ev) => (
                        <EventBlock
                          key={ev.id ?? (ev.title + ev.start)}
                          event={ev}
                          eventColor={getEventColor(ev.payment_status)}
                          eventBorderColor={getEventBorderColor(ev.payment_status)}
                          setIsBookingModalOpen={setIsBookingModalOpen}
                          setSelectedBookingId={setSelectedBookingId}
                          compact
                        />
                      ))}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {isBookingModalOpen && (
        <BookingDetailsModal
          open={isBookingModalOpen}
          handleOnCancel={handleBookingCancel}
          bookingUuid={selectedBookingId}
          onCancelSuccess={handleBookingCancelSuccess}
        />
      )}
    </div>
  );
};

CrewWeekGrid.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
};

export default CrewWeekGrid;
